import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { productCategories } from '@/mocks/products';

function ProductCategoryRow({
  category,
  index,
}: {
  category: (typeof productCategories)[0];
  index: number;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const { t } = useTranslation();

  const checkScroll = () => {
    if (!trackRef.current) return;
    const el = trackRef.current;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  const scroll = (direction: 'left' | 'right') => {
    if (!trackRef.current) return;
    trackRef.current.scrollBy({ left: direction === 'left' ? -320 : 320, behavior: 'smooth' });
    setTimeout(checkScroll, 300);
  };

  const bgClass = index % 2 === 0 ? 'bg-white' : 'bg-stone-50';

  return (
    <section className={`py-10 md:py-14 ${bgClass}`}>
      <div className="mx-auto px-4 md:px-6" style={{ maxWidth: 1280 }}>
        <div className="flex items-center justify-between mb-5 md:mb-6">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 rounded-full bg-emerald-600" />
            <h2 className="text-xl md:text-2xl font-bold text-stone-900 font-heading">
              {category.name}
            </h2>
          </div>
          <a
            href={category.categoryUrl}
            target="_blank"
            rel="noopener nofollow"
            className="text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors whitespace-nowrap flex items-center gap-1"
          >
            {t('categories_cta')}
            <i className="ri-arrow-right-line text-base" />
          </a>
        </div>

        <div className="relative group">
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/90 shadow-md flex items-center justify-center text-stone-700 hover:text-stone-900 transition-all border border-stone-200/80 cursor-pointer"
              aria-label="Scroll left"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
          )}
          <div
            ref={trackRef}
            onScroll={checkScroll}
            className="flex gap-4 overflow-x-auto scroll-smooth pb-2"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', scrollSnapType: 'x mandatory' }}
          >
            {category.products.map((product) => (
              <a
                key={product.id}
                href={product.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex-shrink-0 w-44 md:w-56 group/card cursor-pointer"
                style={{ scrollSnapAlign: 'start' }}
              >
                <div className="bg-white rounded-lg border border-stone-200/80 overflow-hidden transition-shadow hover:shadow-md">
                  <div className="relative w-full h-44 md:h-56 bg-stone-100 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover object-top transition-transform duration-300 group-hover/card:scale-105"
                      loading="lazy"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-black/40 to-transparent" />
                  </div>
                  <div className="p-3 md:p-4">
                    <h3 className="text-xs md:text-sm font-medium text-stone-900 leading-snug line-clamp-2 mb-2 min-h-[2.5rem]">
                      {product.name}
                    </h3>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-emerald-600">{product.price}</span>
                      <span className="text-xs text-stone-500">{product.priceUsd}</span>
                    </div>
                  </div>
                </div>
              </a>
            ))}
            <div className="flex-shrink-0 w-44 md:w-56 flex items-center justify-center">
              <a
                href={category.categoryUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full h-44 md:h-56 rounded-lg border-2 border-dashed border-stone-300 flex flex-col items-center justify-center gap-2 text-stone-500 hover:text-emerald-600 hover:border-emerald-400 transition-colors cursor-pointer bg-stone-50"
              >
                <i className="ri-apps-line text-2xl" />
                <span className="text-sm font-medium text-center px-3">{t('categories_view_all')} {category.name}</span>
              </a>
            </div>
          </div>
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/90 shadow-md flex items-center justify-center text-stone-700 hover:text-stone-900 transition-all border border-stone-200/80 cursor-pointer"
              aria-label="Scroll right"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

export default function ProductCarousels() {
  return (
    <div>
      {productCategories.map((category, index) => (
        <ProductCategoryRow key={category.slug} category={category} index={index} />
      ))}
    </div>
  );
}